/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, HStack, Text, 
  Tabs, TabList, TabPanels, Tab, TabPanel, Input,
  Badge, Box, Textarea, IconButton, Grid,
  useToast, FormControl, FormLabel, FormHelperText, Switch, Spinner,
  Alert, AlertIcon, Circle, Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from "@chakra-ui/react";
import { 
  FiSend, FiClock, FiRefreshCw, 
  FiTerminal, FiActivity, FiLock, FiMapPin,
  FiSettings, FiList
} from "react-icons/fi";
import api from "utils/api";

const cardStyle = {
  bg: "white",
  borderRadius: "lg",
  border: "1px solid",
  borderColor: "gray.200",
  p: 4,
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "-";
  const ts = typeof timestamp === "number" ? timestamp : new Date(timestamp).getTime();
  if (isNaN(ts)) return "-";
  const diff = Date.now() - ts;
  const dk = Math.floor(diff / 60000);
  const saat = Math.floor(diff / 3600000);
  const gun = Math.floor(diff / 86400000);
  if (dk < 1) return "Az önce";
  if (dk < 60) return `${dk} dakika önce`;
  if (saat < 24) return `${saat} saat önce`;
  return `${gun} gün önce`;
};

const SerbestKomutModal = ({ isOpen, onClose, aracData }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [komutSonuclari, setKomutSonuclari] = useState([]);

  const aktivlikDurumu = useMemo(() => {
    if (!aracData) return { online: false, lastSeen: "-" };
    const ts = aracData._son_aktiflik_timestamp ?? aracData.server_time;
    const lastTs = ts ? (typeof ts === "number" ? ts : new Date(ts).getTime()) : null;
    const online = lastTs && !isNaN(lastTs) && Date.now() - lastTs <= 5 * 60 * 1000;
    const lastSeen = lastTs ? formatTimeAgo(lastTs) : (aracData.son_aktiflik || "-");
    return { online, lastSeen };
  }, [aracData]);
  
  // Form states
  const [selectedKomut, setSelectedKomut] = useState("");
  const [serbbestKomut, setSerbbestKomut] = useState("");
  const [parametreler, setParametreler] = useState({});
  const [zamanlanmis, setZamanlanmis] = useState(false);
  const [zamanlamaTarihi, setZamanlamaTarihi] = useState("");
  const [response, setResponse] = useState(null);
  const [responseLoading, setResponseLoading] = useState(false);
  const [komutSonuclariLoading, setKomutSonuclariLoading] = useState(false);
  const [gonderilenKomutlar, setGonderilenKomutlar] = useState([]);

  // Komut kategorileri
  const komutKategorileri = {
    temel: [
      { kod: "STATUS", isim: "Durum Sorgula", aciklama: "Cihaz durumunu kontrol et" },
      { kod: "RESET", isim: "Yeniden Başlat", aciklama: "Cihazı yeniden başlat" },
      { kod: "REBOOT", isim: "Sistem Reboot", aciklama: "Sistem yeniden başlatma" }
    ],
    lokasyon: [
      { kod: "WHERE", isim: "Konum Al", aciklama: "Güncel GPS koordinatları" },
      { kod: "GPSON", isim: "GPS Aç", aciklama: "GPS modülünü aktif et" },
      { kod: "GPSOFF", isim: "GPS Kapat", aciklama: "GPS modülünü deaktif et" }
    ],
    guvenlik: [
      { kod: "LOCK", isim: "Kilitle", aciklama: "Aracı kilitle", params: ["pin"] },
      { kod: "UNLOCK", isim: "Kilit Aç", aciklama: "Aracın kilidini aç", params: ["pin"] },
      { kod: "ALARM1", isim: "Alarm Aktif", aciklama: "Alarmı etkinleştir" }
    ],
    konfig: [
      { kod: "APN", isim: "APN Ayarla", aciklama: "Internet APN ayarları", params: ["apn", "user", "pass"] },
      { kod: "INTERVAL", isim: "Raporlama Süresi", aciklama: "Veri gönderme aralığı", params: ["saniye"] }
    ]
  };

  const loadKomutGecmisi = useCallback(async () => {
    if (!aracData?.cihaz_id) return;
    setKomutSonuclariLoading(true);
    try {
      const now = new Date();
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().replace(/\.\d{3}Z$/, ".000Z");
      const to = now.toISOString().replace(/\.\d{3}Z$/, ".999Z");
      const res = await api.get(
        `/api/telemetri/komut-sonuclari?deviceId=${aracData.cihaz_id}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
      );
      const data = await res.json();
      const liste = Array.isArray(data) ? data : [];
      setKomutSonuclari(liste);
    } catch {
      setKomutSonuclari([]);
    } finally {
      setKomutSonuclariLoading(false);
    }
  }, [aracData?.cihaz_id]);

  useEffect(() => {
    if (isOpen && aracData) {
      loadKomutGecmisi();
      setGonderilenKomutlar([]);
    }
  }, [isOpen, aracData?.cihaz_id, loadKomutGecmisi]);

  const handleKomutGonder = async () => {
    setLoading(true);
    setResponseLoading(true);
    
    try {
      let gonderilecekKomut = activeTab === 4 ? serbbestKomut : selectedKomut;
      
      // Parametreleri ekle
      if (Object.keys(parametreler).length > 0) {
        const params = Object.values(parametreler).join(",");
        gonderilecekKomut += `=${params}`;
      }

      // API call
      const response = await api.post(`/api/telemetri/komut`, {
        imei: aracData?.imei,
        komut: gonderilecekKomut,
        zamanlanmis: zamanlanmis ? zamanlamaTarihi : null
      });

      const result = await response.json();

      toast({
        title: "Komut Gönderildi",
        description: `${gonderilecekKomut} komutu başarıyla gönderildi`,
        status: "success",
        duration: 3000
      });

      setResponse({
        success: true,
        data: result.response || result.message || "Komut gönderildi",
        timestamp: new Date().toLocaleString("tr-TR")
      });
      setGonderilenKomutlar(prev => [{ komut: gonderilecekKomut, zaman: new Date() }, ...prev]);
      setResponseLoading(false);
      loadKomutGecmisi();

    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || "Komut gönderilemedi";
      toast({
        title: "Hata",
        description: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
        status: "error",
        duration: 4000
      });
      setResponseLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const renderKomutListesi = (kategori) => (
    <VStack spacing={2} align="stretch">
      {komutKategorileri[kategori].map((komut) => (
        <Box
          key={komut.kod}
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor={selectedKomut === komut.kod ? "navy.400" : "gray.200"}
          bg={selectedKomut === komut.kod ? "navy.50" : "white"}
          cursor="pointer"
          onClick={() => {
            setSelectedKomut(komut.kod);
            setParametreler({});
          }}
          _hover={{ borderColor: "gray.300", bg: "gray.50" }}
        >
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Text fontWeight="semibold" fontSize="sm">{komut.isim}</Text>
              <Text fontSize="xs" color="gray.600">{komut.aciklama}</Text>
            </VStack>
            <Text fontSize="xs" fontFamily="mono" color="gray.700" bg="gray.100" px={2} py={1} borderRadius="md">
              {komut.kod}
            </Text>
          </HStack>
          
          {/* Parametre girişleri */}
          {selectedKomut === komut.kod && komut.params && (
            <VStack spacing={2} mt={3}>
              {komut.params.map((param) => (
                <FormControl key={param} size="sm">
                  <FormLabel fontSize="xs" textTransform="capitalize">{param}</FormLabel>
                  <Input
                    size="sm"
                    placeholder={`${param} değeri`}
                    onChange={(e) => setParametreler(prev => ({ ...prev, [param]: e.target.value }))}
                  />
                </FormControl>
              ))}
            </VStack>
          )}
        </Box>
      ))}
    </VStack>
  );

  if (!aracData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1} border="1px solid" borderColor="gray.200">
        
        {/* Header - Araç Bilgileri - Temaya uyumlu */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.200" p={4}>
          <ModalCloseButton size="sm" color="gray.500" _hover={{ color: "gray.700" }} />
          <Grid templateColumns="2fr 1fr" gap={4} alignItems="center">
            <VStack align="start" spacing={2}>
              <HStack spacing={3}>
                <Box p={2} bg="gray.50" borderRadius="md">
                  <FiTerminal size={20} color="#1f2733" />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    Telemetri Komut Merkezi
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {aracData.plaka} • {aracData.imei}
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={2}>
                <Badge variant="subtle" colorScheme="gray" fontSize="xs" px={2} py={0.5}>
                  {aracData.firma || "Firma Yok"}
                </Badge>
                <Badge variant="subtle" colorScheme="gray" fontSize="xs" px={2} py={0.5}>
                  {aracData.cihaz_tipi || "Cihaz"}
                </Badge>
              </HStack>
            </VStack>
            
            {/* Durum - Araç aktiflik (aracData'dan çekiliyor) */}
            <HStack
              spacing={3}
              px={3}
              py={2}
              bg="gray.50"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <HStack spacing={1.5}>
                <Circle size="1.5" bg={aktivlikDurumu.online ? "green.400" : "red.400"} />
                <Text fontSize="xs" color="gray.700" fontWeight="500">
                  {aktivlikDurumu.online ? "Çevrimiçi" : "Çevrimdışı"}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">•</Text>
              <Text fontSize="xs" color="gray.500">{aktivlikDurumu.lastSeen}</Text>
            </HStack>
          </Grid>
        </Box>

        <ModalBody p={6} bg="gray.50">
          <Grid templateColumns="1fr 1fr" gap={6} h="500px">
            {/* Sol Panel - Komut Gönderme */}
            <Box>
              <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
                <TabList>
                  <Tab fontSize="sm" leftIcon={<FiActivity />}>Temel</Tab>
                  <Tab fontSize="sm" leftIcon={<FiMapPin />}>Konum</Tab>
                  <Tab fontSize="sm" leftIcon={<FiLock />}>Güvenlik</Tab>
                  <Tab fontSize="sm" leftIcon={<FiSettings />}>Ayarlar</Tab>
                  <Tab fontSize="sm" leftIcon={<FiTerminal />}>Serbest</Tab>
                  <Tab fontSize="sm" leftIcon={<FiList />}>Komut Sonuçları</Tab>
                </TabList>
                
                <TabPanels>
                  <TabPanel px={0}>{renderKomutListesi("temel")}</TabPanel>
                  <TabPanel px={0}>{renderKomutListesi("lokasyon")}</TabPanel>
                  <TabPanel px={0}>{renderKomutListesi("guvenlik")}</TabPanel>
                  <TabPanel px={0}>{renderKomutListesi("konfig")}</TabPanel>
                  <TabPanel px={0}>
                    <Box {...cardStyle}>
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={2}>
                          Serbest Komut
                        </FormLabel>
                        <Textarea
                          value={serbbestKomut}
                          onChange={(e) => setSerbbestKomut(e.target.value)}
                          placeholder="Örnek: AT+CSQ&#13;&#10;AT+CGMI"
                          rows={6}
                          size="sm"
                          fontFamily="mono"
                          fontSize="sm"
                          borderRadius="md"
                          borderColor="gray.200"
                          _hover={{ borderColor: "gray.300" }}
                          _focus={{ borderColor: "navy.400", boxShadow: "0 0 0 1px var(--chakra-colors-navy-400)" }}
                          resize="vertical"
                          minH="120px"
                        />
                        <FormHelperText fontSize="xs" color="gray.500" mt={2}>
                          Protokol komutlarını satır satır veya tek satırda girebilirsiniz.
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  </TabPanel>
                  <TabPanel px={0}>
                    <Box {...cardStyle}>
                      <HStack justify="space-between" mb={3}>
                        <Text fontWeight="semibold" fontSize="sm">Komut Sonuçları</Text>
                        <IconButton size="xs" icon={<FiRefreshCw />} onClick={loadKomutGecmisi} isLoading={komutSonuclariLoading} aria-label="Yenile" />
                      </HStack>
                      <TableContainer maxH="320px" overflowY="auto">
                        <Table size="sm" variant="simple">
                          <Thead position="sticky" top={0} bg="white" zIndex={1}>
                            <Tr>
                              <Th>Cihaz</Th>
                              <Th>Son güncelleme zamanı</Th>
                              <Th>Tip</Th>
                              <Th>Adres</Th>
                              <Th>Veri</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {komutSonuclariLoading ? (
                              <Tr><Td colSpan={5} textAlign="center" py={6}>Yükleniyor...</Td></Tr>
                            ) : komutSonuclari.length === 0 ? (
                              <Tr><Td colSpan={5} textAlign="center" py={6} color="gray.500">Henüz komut sonucu yok (son 24 saat)</Td></Tr>
                            ) : (
                              [...komutSonuclari].reverse().map((e) => (
                                <Tr key={e.id}>
                                  <Td>{aracData?.plaka || aracData?.imei || "-"}</Td>
                                  <Td>{e.eventTime ? new Date(e.eventTime).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-"}</Td>
                                  <Td>Komut sonucu</Td>
                                  <Td>-</Td>
                                  <Td fontFamily="mono" fontSize="xs" whiteSpace="pre-wrap" maxW="300px">{e.attributes?.result ?? "-"}</Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>

              {/* Zamanlama */}
              <Box mt={4} {...cardStyle} p={3}>
                <HStack justify="space-between">
                  <HStack>
                    <FiClock />
                    <Text fontSize="sm">Zamanlanmış Gönderim</Text>
                  </HStack>
                  <Switch
                    colorScheme="blue"
                    isChecked={zamanlanmis}
                    onChange={(e) => setZamanlanmis(e.target.checked)}
                  />
                </HStack>
                {zamanlanmis && (
                  <Input
                    type="datetime-local"
                    size="sm"
                    mt={2}
                    value={zamanlamaTarihi}
                    onChange={(e) => setZamanlamaTarihi(e.target.value)}
                  />
                )}
              </Box>
            </Box>

            {/* Sağ Panel - Cihaz Yanıtı & Gönderilen Komutlar */}
            <VStack spacing={4} align="stretch">
              <Box {...cardStyle}>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="semibold" fontSize="sm">Cihaz Yanıtı</Text>
                  {responseLoading && <Spinner size="sm" />}
                </HStack>
                
                {response ? (
                  <Alert status={response.success ? "success" : "error"} borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm">{response.data}</Text>
                      <Text fontSize="xs" color="gray.600">{response.timestamp}</Text>
                    </VStack>
                  </Alert>
                ) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                    Henüz yanıt yok
                  </Text>
                )}
              </Box>

              {/* Gönderilen Komutlar (memory) */}
              <Box {...cardStyle} flex="1" minH={0} d="flex" flexDirection="column" overflow="hidden">
                <Text fontWeight="semibold" fontSize="sm" mb={3}>Gönderilen Komutlar</Text>
                <Box flex="1" minH={0} overflowY="auto">
                  {gonderilenKomutlar.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                      Henüz komut gönderilmedi
                    </Text>
                  ) : (
                    <VStack spacing={2} align="stretch" pb={2}>
                      {gonderilenKomutlar.map((item, i) => (
                        <Box
                          key={i}
                          w="full"
                          p={3}
                          bg="gray.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <Text fontSize="xs" fontFamily="mono" color="gray.700" wordBreak="break-word" whiteSpace="pre-wrap">
                            {item.komut}
                          </Text>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {item.zaman.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              </Box>
            </VStack>
          </Grid>
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4} borderTop="1px solid" borderColor="gray.200">
          <HStack spacing={3} w="full" justify="end">
            <Button variant="ghost" onClick={onClose}>İptal</Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiSend />}
              onClick={handleKomutGonder}
              isLoading={loading}
              loadingText="Gönderiliyor"
              isDisabled={!selectedKomut && !serbbestKomut}
            >
              Komut Gönder
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SerbestKomutModal;